/**
 * Customer Review Submission Wizard
 *
 * 3-step wizard for submitting trip reviews:
 * Step 1: Overall rating and category ratings
 * Step 2: Written review and experience details
 * Step 3: Review preview and submit
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-10: Review Submission Wizard (5 SP)
 *
 * Data Flow:
 * - Fetches booking details from GET /api/v1/customer/trips/:bookingId
 * - Submits to POST /api/v1/reviews
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  Star as _Star,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle as _AlertCircle,
  Calendar,
  MapPin
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import StarRating from '../../components/reviews/StarRating';
import { logError } from '../../services/errorLoggingService';

export default function CustomerReviewWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking');

  const [currentStep, setCurrentStep] = useState(1);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Review data
  const [reviewData, setReviewData] = useState({
    overall_rating: 0,
    communication_rating: 0,
    knowledge_rating: 0,
    professionalism_rating: 0,
    value_rating: 0,
    review_text: '',
    would_recommend: null,
    trip_highlights: '',
    areas_improvement: ''
  });

  useEffect(() => {
    if (bookingId) {
      const fetchBookingDetails = async () => {
        try {
          setLoading(true);
          const response = await apiRequest(`/api/v1/customer/trips/${bookingId}`, { method: 'GET' });
          if (response.success) {
            setBooking(response.data);
          }
        } catch (err) {
          logError(err, { componentName: 'CustomerReviewWizard', action: 'fetch_booking' });
          if (err.status === 401) {
            setError('Session expired. Please log in again.');
          } else {
            setError(err.message || 'Failed to load booking details');
          }
        } finally {
          setLoading(false);
        }
      };

      fetchBookingDetails();
    } else {
      setError('No booking specified');
      setLoading(false);
    }
  }, [bookingId]);

  const handleRatingChange = (field, rating) => {
    setReviewData({ ...reviewData, [field]: rating });
  };

  const handleTextChange = (field, value) => {
    setReviewData({ ...reviewData, [field]: value });
  };

  const canProceedToStep2 = () => {
    return reviewData.overall_rating > 0 &&
           reviewData.communication_rating > 0 &&
           reviewData.knowledge_rating > 0 &&
           reviewData.professionalism_rating > 0 &&
           reviewData.value_rating > 0;
  };

  const canProceedToStep3 = () => {
    return reviewData.review_text.trim().length >= 50 &&
           reviewData.would_recommend !== null;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await apiRequest('/api/v1/reviews', {
        method: 'POST',
        body: JSON.stringify({
          booking_id: bookingId,
          ...reviewData
        })
      });

      if (response.success) {
        navigate('/customer/reviews?success=true');
      }
    } catch (err) {
      logError(err, { componentName: 'CustomerReviewWizard', action: 'submit_review' });
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to submit review');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" activeNavId="reviews" onNavigate={handleNavigate}>
        <LoadingSpinner message="Loading..." className="min-h-screen" />
      </DashboardLayout>
    );
  }

  if (error || !booking) {
    return (
      <DashboardLayout userRole="customer" activeNavId="reviews" onNavigate={handleNavigate}>
        <ErrorAlert message={error || 'Booking not found'} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole="customer"
      activeNavId="reviews"
      onNavigate={handleNavigate}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/customer/reviews')}
            className="text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            ← Back to Reviews
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Write a Review</h1>
          <p className="mt-1 text-sm text-gray-500">
            Share your experience to help other adventurers
          </p>
        </div>

        {/* Trip Summary */}
        <Card>
          <div className="flex items-center space-x-4">
            {booking.experience_image && (
              <img
                src={booking.experience_image}
                alt={booking.experience_title}
                className="w-20 h-20 rounded-lg object-cover"
              />
            )}
            <div>
              <h3 className="font-semibold text-gray-900">{booking.experience_title}</h3>
              <p className="text-sm text-gray-600 mt-1">with {booking.guide_name}</p>
              <div className="flex items-center space-x-3 mt-2 text-sm text-gray-500">
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(booking.start_date)}
                </span>
                <span className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {booking.location}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Progress Steps */}
        <Card>
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep === step
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : currentStep > step
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-gray-300 bg-white text-gray-500'
                }`}>
                  {currentStep > step ? <Check className="w-5 h-5" /> : step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    currentStep > step ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3">
            <span className="text-sm font-medium text-gray-700">Ratings</span>
            <span className="text-sm font-medium text-gray-700">Details</span>
            <span className="text-sm font-medium text-gray-700">Review</span>
          </div>
        </Card>

        {/* Step 1: Ratings */}
        {currentStep === 1 && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Rate Your Experience</h2>
            <div className="space-y-6">
              <StarRating
                value={reviewData.overall_rating}
                onChange={(rating) => handleRatingChange('overall_rating', rating)}
                label="Overall Experience"
              />
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Rate Specific Aspects</h3>
                <div className="space-y-4">
                  <StarRating
                    value={reviewData.communication_rating}
                    onChange={(rating) => handleRatingChange('communication_rating', rating)}
                    label="Communication"
                  />
                  <StarRating
                    value={reviewData.knowledge_rating}
                    onChange={(rating) => handleRatingChange('knowledge_rating', rating)}
                    label="Knowledge & Expertise"
                  />
                  <StarRating
                    value={reviewData.professionalism_rating}
                    onChange={(rating) => handleRatingChange('professionalism_rating', rating)}
                    label="Professionalism"
                  />
                  <StarRating
                    value={reviewData.value_rating}
                    onChange={(rating) => handleRatingChange('value_rating', rating)}
                    label="Value for Money"
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Step 2: Written Review */}
        {currentStep === 2 && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Tell Us About Your Experience</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review *
                </label>
                <textarea
                  value={reviewData.review_text}
                  onChange={(e) => handleTextChange('review_text', e.target.value)}
                  rows={6}
                  placeholder="Share details about your trip, what you enjoyed, and what made it memorable..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  {reviewData.review_text.length} / 50 minimum characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Would you recommend this guide? *
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => handleTextChange('would_recommend', true)}
                    className={`flex-1 px-4 py-3 border-2 rounded-lg font-medium transition-colors ${
                      reviewData.would_recommend === true
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    Yes, Recommend
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTextChange('would_recommend', false)}
                    className={`flex-1 px-4 py-3 border-2 rounded-lg font-medium transition-colors ${
                      reviewData.would_recommend === false
                        ? 'border-red-600 bg-red-50 text-red-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    No, Not Recommend
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trip Highlights (Optional)
                </label>
                <textarea
                  value={reviewData.trip_highlights}
                  onChange={(e) => handleTextChange('trip_highlights', e.target.value)}
                  rows={3}
                  placeholder="What were the best parts of your trip?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Areas for Improvement (Optional)
                </label>
                <textarea
                  value={reviewData.areas_improvement}
                  onChange={(e) => handleTextChange('areas_improvement', e.target.value)}
                  rows={3}
                  placeholder="How could the experience be better?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Preview */}
        {currentStep === 3 && (
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Review Preview</h2>
            <div className="space-y-6">
              {/* Overall Rating */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Overall Rating</h3>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <_Star
                      key={star}
                      className={`w-6 h-6 ${
                        star <= reviewData.overall_rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="text-lg font-bold text-gray-900">{reviewData.overall_rating}.0</span>
                </div>
              </div>

              {/* Category Ratings */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Category Ratings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Communication</p>
                    <p className="font-medium">{reviewData.communication_rating}.0</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Knowledge</p>
                    <p className="font-medium">{reviewData.knowledge_rating}.0</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Professionalism</p>
                    <p className="font-medium">{reviewData.professionalism_rating}.0</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Value</p>
                    <p className="font-medium">{reviewData.value_rating}.0</p>
                  </div>
                </div>
              </div>

              {/* Review Text */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Your Review</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {reviewData.review_text}
                </p>
              </div>

              {/* Recommendation */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Recommendation:</span>{' '}
                  {reviewData.would_recommend ? (
                    <span className="text-green-600">Yes, I would recommend this guide</span>
                  ) : (
                    <span className="text-red-600">No, I would not recommend this guide</span>
                  )}
                </p>
              </div>

              {reviewData.trip_highlights && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Trip Highlights</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {reviewData.trip_highlights}
                  </p>
                </div>
              )}

              {reviewData.areas_improvement && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Areas for Improvement</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {reviewData.areas_improvement}
                  </p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          {currentStep < 3 ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={
                (currentStep === 1 && !canProceedToStep2()) ||
                (currentStep === 2 && !canProceedToStep3())
              }
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Submit Review</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
